import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { ContactUpsertModalService } from '../contact-list/contact-upsert-modal.service';

export type InlineAddKind = 'caretaker' | 'propertyManager' | 'technicalAssetsManager' | 'sevManager' | 'accountant' | 'service';

@Injectable()
export class PropertyInlineContactAddService {
  constructor(private contactUpsertModal: ContactUpsertModalService) {}

  async addForProperty(options: {
    kind: InlineAddKind;
    form: FormGroup;
    controlName: string;
    forcedServiceType?: string;
    reloadOptions: () => Promise<void>;
    onCreated?: (createdId: string) => void;
  }): Promise<void> {
    const { kind, form, controlName, forcedServiceType, reloadOptions, onCreated } = options;

    const modalData = this.buildModalData(kind, forcedServiceType);
    const result = await firstValueFrom(this.contactUpsertModal.open(modalData));

    if (!result?.entity) {
      return;
    }

    await reloadOptions();

    const createdId = result.entity?._id || result.entity?.id;
    if (!createdId) {
      return;
    }

    onCreated?.(String(createdId));

    const control = form.get(controlName);
    if (!control) {
      return;
    }

    if (kind === 'service') {
      const current = Array.isArray(control.value) ? control.value : [];
      control.setValue(Array.from(new Set([...current, createdId])));
    } else {
      control.setValue(createdId);
    }

    control.markAsDirty();
    control.updateValueAndValidity();
  }

  private buildModalData(kind: InlineAddKind, forcedServiceType?: string): any {
    if (kind === 'caretaker') {
      return { type: 'caretaker', origin: 'property-list' };
    }

    if (kind === 'propertyManager') {
      return {
        type: 'employee',
        origin: 'property-list',
        lockRole: true,
        forcedRole: 'Property Manager'
      };
    }

    if (kind === 'technicalAssetsManager') {
      return {
        type: 'employee',
        origin: 'property-list',
        lockRole: true,
        forcedRole: 'Technical Assets Manager'
      };
    }

    if (kind === 'sevManager') {
      return {
        type: 'employee',
        origin: 'property-list',
        lockRole: true,
        forcedRole: 'Condominium Manager'
      };
    }

    if (kind === 'accountant') {
      return {
        type: 'employee',
        origin: 'property-list',
        lockRole: true,
        forcedRole: 'Accountant'
      };
    }

    return {
      type: 'service',
      origin: 'property-list',
      lockServiceType: true,
      forcedServiceType: forcedServiceType || null
    };
  }
}
